import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { XMarkIcon, TruckIcon, MapPinIcon, CalendarIcon, ClockIcon, UserIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Trip, TripFormData, Bus } from '../../types';
import { FormField, Input, Select, FormCard, MultiRouteForm, DateInput } from '../../components/Forms';

interface TripFormProps {
  trip?: Trip;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (trip: Trip) => void;
}

const TripForm: React.FC<TripFormProps> = ({ trip, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<TripFormData>({
    date: '',
    routes: [
      {
        from: 'حائل',
        to: 'مكة المكرمة',
        dateFrom: '',
        dateTo: '',
        timeFrom: '',
        timeTo: '',
        distance: 650,
        isActive: true
      }
    ],
    buses: [],
    // Legacy fields for backward compatibility
    bus: '',
    driver: {
      name: '',
      phone: '',
      licenseNumber: ''
    },
    supervisor: {
      name: '',
      phone: ''
    },
    status: 'draft'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedBus, setSelectedBus] = useState<Bus | null>(null);

  // Route management functions
  const addRoute = () => {
    setFormData(prev => ({
      ...prev,
      routes: [
        ...prev.routes,
        {
          from: '',
          to: '',
          dateFrom: '',
          dateTo: '',
          timeFrom: '',
          timeTo: '',
          distance: 0,
          isActive: true
        }
      ]
    }));
  };

  const removeRoute = (index: number) => {
    if (formData.routes.length > 1) {
      setFormData(prev => ({
        ...prev,
        routes: prev.routes.filter((_, i) => i !== index)
      }));
    }
  };

  const updateRoute = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      routes: prev.routes.map((route, i) => 
        i === index ? { ...route, [field]: value } : route
      )
    }));
  };

  const queryClient = useQueryClient();

  // Fetch available buses
  const { data: busesData, isLoading: busesLoading } = useQuery({
    queryKey: ['buses', 'available'],
    queryFn: () => apiService.getAvailableBuses(),
    enabled: isOpen,
  });

  const buses = busesData || [];

  // Create/Update trip mutation
  const tripMutation = useMutation({
    mutationFn: (data: TripFormData) => {
      if (trip) {
        return apiService.updateTrip(trip._id, data);
      } else {
        return apiService.createTrip(data);
      }
    },
    onSuccess: (newTrip) => {
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      onSuccess(newTrip);
      onClose();
    },
    onError: (error: any) => {
      console.error('Trip mutation error:', error);
      setErrors({ general: 'حدث خطأ أثناء حفظ الرحلة' });
    },
  });

  // Initialize form data when trip prop changes
  useEffect(() => {
    if (trip) {
      // Handle both new multi-bus format and legacy single bus format
      const buses = trip.buses && trip.buses.length > 0 
        ? trip.buses.map(tripBus => ({
            busId: tripBus.bus._id,
            driver: {
              name: tripBus.driver.name,
              phone: tripBus.driver.phone,
              licenseNumber: tripBus.driver.licenseNumber
            },
            supervisor: tripBus.supervisor ? {
              name: tripBus.supervisor.name,
              phone: tripBus.supervisor.phone
            } : undefined
          }))
        : trip.bus && trip.driver ? [{
            busId: trip.bus._id,
            driver: {
              name: trip.driver.name,
              phone: trip.driver.phone,
              licenseNumber: trip.driver.licenseNumber
            },
            supervisor: trip.supervisor ? {
              name: trip.supervisor.name,
              phone: trip.supervisor.phone
            } : undefined
          }] : [];

      setFormData({
        date: trip.date ? new Date(trip.date).toISOString().split('T')[0] : '',
        routes: trip.routes || (trip.route ? [{
          from: trip.route.from,
          to: trip.route.to,
          dateFrom: trip.route.departureTime ? new Date(trip.route.departureTime).toISOString().split('T')[0] : '',
          dateTo: trip.route.arrivalTime ? new Date(trip.route.arrivalTime).toISOString().split('T')[0] : '',
          timeFrom: trip.route.departureTime ? new Date(trip.route.departureTime).toISOString().slice(11, 16) : '',
          timeTo: trip.route.arrivalTime ? new Date(trip.route.arrivalTime).toISOString().slice(11, 16) : '',
          distance: trip.route.distance || 0,
          isActive: true
        }] : []),
        buses,
        // Legacy fields for backward compatibility
        bus: trip.bus?._id || '',
        driver: trip.driver ? {
          name: trip.driver.name,
          phone: trip.driver.phone,
          licenseNumber: trip.driver.licenseNumber
        } : { name: '', phone: '', licenseNumber: '' },
        supervisor: trip.supervisor ? {
          name: trip.supervisor.name,
          phone: trip.supervisor.phone
        } : { name: '', phone: '' },
        status: trip.status
      });
      setSelectedBus(trip.bus || null);
    } else {
      // Reset form for new trip
      setFormData({
        date: '',
        routes: [
          {
            from: 'حائل',
            to: 'مكة المكرمة',
            dateFrom: '',
            dateTo: '',
            timeFrom: '',
            timeTo: '',
            distance: 650,
            isActive: true
          }
        ],
        buses: [],
        // Legacy fields for backward compatibility
        bus: '',
        driver: {
          name: '',
          phone: '',
          licenseNumber: ''
        },
        supervisor: {
          name: '',
          phone: ''
        },
        status: 'draft'
      });
      setSelectedBus(null);
    }
    setErrors({});
  }, [trip, isOpen]);

  // Handle bus selection (legacy)
  const handleBusChange = (busId: string) => {
    const bus = buses.find(b => b._id === busId);
    setSelectedBus(bus || null);
    
    if (bus) {
      setFormData(prev => ({
        ...prev,
        bus: busId,
        driver: {
          name: bus.driver.name,
          phone: bus.driver.phone,
          licenseNumber: bus.driver.licenseNumber
        },
        supervisor: bus.supervisor ? {
          name: bus.supervisor.name,
          phone: bus.supervisor.phone
        } : { name: '', phone: '' }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        bus: busId,
        driver: { name: '', phone: '', licenseNumber: '' },
        supervisor: { name: '', phone: '' }
      }));
    }
  };

  // Multi-bus management functions
  const addBus = () => {
    setFormData(prev => ({
      ...prev,
      buses: [...prev.buses, {
        busId: '',
        driver: {
          name: '',
          phone: '',
          licenseNumber: ''
        },
        supervisor: {
          name: '',
          phone: ''
        }
      }]
    }));
  };

  const removeBus = (index: number) => {
    setFormData(prev => ({
      ...prev,
      buses: prev.buses.filter((_, i) => i !== index)
    }));
  };

  const updateBus = (index: number, field: string, value: string) => {
    if (field === 'busId') {
      const bus = buses.find(b => b._id === value);
      setFormData(prev => ({
        ...prev,
        buses: prev.buses.map((busData, i) => 
          i === index ? {
            ...busData,
            busId: value,
            // Auto-fill driver info from bus data
            driver: bus ? {
              name: bus.driver.name,
              phone: bus.driver.phone,
              licenseNumber: bus.driver.licenseNumber
            } : busData.driver,
            supervisor: bus?.supervisor ? {
              name: bus.supervisor.name,
              phone: bus.supervisor.phone
            } : busData.supervisor
          } : busData
        )
      }));
    }
  };

  const updateBusDriver = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      buses: prev.buses.map((busData, i) => 
        i === index ? {
          ...busData,
          driver: {
            ...busData.driver!,
            [field]: value
          }
        } : busData
      )
    }));
  };

  const updateBusSupervisor = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      buses: prev.buses.map((busData, i) => 
        i === index ? {
          ...busData,
          supervisor: {
            name: busData.supervisor?.name || '',
            phone: busData.supervisor?.phone || '',
            [field]: value
          }
        } : busData
      )
    }));
  };



  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = 'تاريخ الرحلة مطلوب';
    }

    // Validate routes
    if (formData.routes.length === 0) {
      newErrors.routes = 'يجب إضافة مسار واحد على الأقل';
    }

    // Validate each route
    formData.routes.forEach((route, index) => {
      if (!route.from.trim()) {
        newErrors[`routes.${index}.from`] = 'مدينة المغادرة مطلوبة';
      }
      if (!route.to.trim()) {
        newErrors[`routes.${index}.to`] = 'مدينة الوصول مطلوبة';
      }
      if (!route.dateFrom.trim()) {
        newErrors[`routes.${index}.dateFrom`] = 'تاريخ البداية مطلوب';
      }
      if (!route.dateTo.trim()) {
        newErrors[`routes.${index}.dateTo`] = 'تاريخ النهاية مطلوب';
      }
      if (
        route.dateFrom &&
        route.dateTo &&
        route.dateFrom > route.dateTo
      ) {
        newErrors[`routes.${index}.dateTo`] =
          'تاريخ النهاية يجب أن يكون بعد تاريخ البداية';
      }
      if (!route.timeFrom.trim()) {
        newErrors[`routes.${index}.timeFrom`] = 'وقت المغادرة مطلوب';
      }
      if (!route.timeTo.trim()) {
        newErrors[`routes.${index}.timeTo`] = 'وقت الوصول مطلوب';
      }
      if (route.from === route.to) {
        newErrors[`routes.${index}.to`] = 'مدينة الوصول يجب أن تكون مختلفة عن مدينة المغادرة';
      }
    });

    // Validate buses
    if (formData.buses.length === 0) {
      newErrors.buses = 'يجب إضافة حافلة واحدة على الأقل';
    }

    // Validate each bus
    formData.buses.forEach((busData, index) => {
      if (!busData.busId) {
        newErrors[`bus_${index}`] = 'اختيار الحافلة مطلوب';
      }

      if (!busData.driver?.name) {
        newErrors[`driver_name_${index}`] = 'اسم السائق مطلوب';
      }

      if (!busData.driver?.phone) {
        newErrors[`driver_phone_${index}`] = 'رقم جوال السائق مطلوب';
      }

      if (!busData.driver?.licenseNumber) {
        newErrors[`driver_license_${index}`] = 'رقم رخصة السائق مطلوب';
      }
    });

    // Legacy validation for backward compatibility
    if (!formData.bus && formData.buses.length === 0) {
      newErrors.bus = 'اختيار الحافلة مطلوب';
    }

    if (!formData.driver?.name && formData.buses.length === 0) {
      newErrors.driverName = 'اسم السائق مطلوب';
    }

    if (!formData.driver?.phone && formData.buses.length === 0) {
      newErrors.driverPhone = 'رقم جوال السائق مطلوب';
    }

    if (!formData.driver?.licenseNumber && formData.buses.length === 0) {
      newErrors.driverLicense = 'رقم رخصة السائق مطلوب';
    }

    // Additional validation for routes can be added here if needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await tripMutation.mutateAsync(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const destinationOptions = [
    { value: 'مكة المكرمة', label: 'مكة المكرمة' },
    { value: 'المدينة المنورة', label: 'المدينة المنورة' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'مسودة' },
    { value: 'published', label: 'منشورة' },
    { value: 'full', label: 'مكتملة العدد' },
    { value: 'ongoing', label: 'جارية' },
    { value: 'completed', label: 'مكتملة' },
    { value: 'cancelled', label: 'ملغية' }
  ];

  const busOptions = buses.map(bus => ({
    value: bus._id,
    label: `${bus.busNumber} - ${bus.plateNumber} (${bus.capacity} مقعد)`
  }));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {trip ? 'تعديل الرحلة' : 'رحلة جديدة'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">{errors.general}</p>
            </div>
          )}

          {/* Trip Basic Info */}
          <FormCard title="معلومات الرحلة الأساسية" icon={<CalendarIcon className="w-5 h-5 text-primary-600" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField required error={errors.date}>
                <DateInput
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      تاريخ الرحلة <span className="text-red-500">*</span>
                    </span>
                  }
                  value={formData.date}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  error={!!errors.date}
                />
              </FormField>

              <FormField label="حالة الرحلة" required>
                <Select
                  value={formData.status || 'draft'}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                  options={statusOptions}
                />
              </FormField>
            </div>
          </FormCard>

          {/* Routes Information */}
          <FormCard title="مسارات الرحلة" icon={<MapPinIcon className="w-5 h-5 text-primary-600" />}>
            <MultiRouteForm
              routes={formData.routes}
              onRoutesChange={(routes) => setFormData(prev => ({ ...prev, routes }))}
              errors={errors}
            />
          </FormCard>

          {/* Multi-Bus Selection */}
          <FormCard title="اختيار الحافلات" icon={<TruckIcon className="w-5 h-5 text-primary-600" />}>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-900">الحافلات المختارة ({formData.buses.length})</h4>
                <button
                  type="button"
                  onClick={() => addBus()}
                  className="btn-secondary text-sm flex items-center"
                >
                  <PlusIcon className="w-4 h-4 ml-1" />
                  إضافة حافلة
                </button>
              </div>

              {formData.buses.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <TruckIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>لم يتم اختيار أي حافلة بعد</p>
                  <p className="text-sm">انقر على "إضافة حافلة" لبدء إضافة الحافلات</p>
                </div>
              )}

              {formData.buses.map((busData, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h5 className="font-medium text-gray-900">الحافلة {index + 1}</h5>
                    <button
                      type="button"
                      onClick={() => removeBus(index)}
                      className="text-red-600 hover:text-red-800"
                      title="إزالة الحافلة"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="الحافلة" required error={errors[`bus_${index}`]}>
                      <Select
                        value={busData.busId}
                        onChange={(e) => updateBus(index, 'busId', e.target.value)}
                        options={busOptions}
                        placeholder={busesLoading ? "جاري تحميل الحافلات..." : "اختر الحافلة"}
                        disabled={busesLoading}
                        error={!!errors[`bus_${index}`]}
                      />
                    </FormField>

                    <div className="space-y-2">
                      {busData.busId && (() => {
                        const bus = buses.find(b => b._id === busData.busId);
                        return bus ? (
                          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                            <div className="grid grid-cols-2 gap-2">
                              <div><span className="font-medium">رقم:</span> {bus.busNumber}</div>
                              <div><span className="font-medium">لوحة:</span> {bus.plateNumber}</div>
                              <div><span className="font-medium">سعة:</span> {bus.capacity} مقعد</div>
                              <div><span className="font-medium">حالة:</span> {bus.status}</div>
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>

                  {/* Driver Information for this bus */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField label="اسم السائق" required error={errors[`driver_name_${index}`]}>
                      <Input
                        value={busData.driver?.name || ''}
                        onChange={(e) => updateBusDriver(index, 'name', e.target.value)}
                        placeholder="اسم السائق"
                        error={!!errors[`driver_name_${index}`]}
                      />
                    </FormField>

                    <FormField label="رقم الجوال" required error={errors[`driver_phone_${index}`]}>
                      <Input
                        value={busData.driver?.phone || ''}
                        onChange={(e) => updateBusDriver(index, 'phone', e.target.value)}
                        placeholder="05xxxxxxxx"
                        error={!!errors[`driver_phone_${index}`]}
                      />
                    </FormField>

                    <FormField label="رقم الرخصة" required error={errors[`driver_license_${index}`]}>
                      <Input
                        value={busData.driver?.licenseNumber || ''}
                        onChange={(e) => updateBusDriver(index, 'licenseNumber', e.target.value)}
                        placeholder="رقم رخصة القيادة"
                        error={!!errors[`driver_license_${index}`]}
                      />
                    </FormField>
                  </div>

                  {/* Supervisor Information for this bus (Optional) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="اسم المشرف (اختياري)">
                      <Input
                        value={busData.supervisor?.name || ''}
                        onChange={(e) => updateBusSupervisor(index, 'name', e.target.value)}
                        placeholder="اسم المشرف"
                      />
                    </FormField>

                    <FormField label="رقم جوال المشرف">
                      <Input
                        value={busData.supervisor?.phone || ''}
                        onChange={(e) => updateBusSupervisor(index, 'phone', e.target.value)}
                        placeholder="05xxxxxxxx"
                      />
                    </FormField>
                  </div>
                </div>
              ))}

              {errors.buses && (
                <div className="text-red-600 text-sm">{errors.buses}</div>
              )}
            </div>
          </FormCard>



          {/* Form Actions */}
          <div className="flex justify-end space-x-4 space-x-reverse pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={tripMutation.isPending}
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={tripMutation.isPending}
            >
              {tripMutation.isPending ? 'جاري الحفظ...' : (trip ? 'تحديث الرحلة' : 'إنشاء الرحلة')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TripForm; 